import axios, { AxiosRequestConfig } from 'axios';
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/users/me/profile',
    login: '/auth/admin/signin-email',
    refreshToken: '/auth/refresh-token',
    generateOTP: '/auth/generate-otp',
    verifyOTP: '/auth/verify-otp',
    resetPassword: '/auth/reset-password',
    newPassword: '/auth/new-password',
    forgotPassword: '/auth/forgot-password',
    confirmRegister: '/auth/confirm-register',
    resendCodeRegister: '/auth/resend-code-register',
  },

  reports: {
    activeLocation: '/reports/active-location', // ดึงรายงานจำนวน Location ที่ Active อยู่
    garbageCollection: '/reports/garbage-collection', // ดึงรายงานปริมาณขยะ
    garbageByLocation: '/reports/garbage-by-location', // ดึงรายงานปริมาณขยะแยกตาม Location
    garbageByManager: '/reports/garbage-by-manager', // ดึงรายงานปริมาณขยะแยกตาม Manager
    garbageByUser: '/reports/garbage-by-user', // ดึงรายงานปริมาณขยะแยกตาม (ผู้ใช้งาน)
    garbageByType: '/reports/garbage-by-type', // ดึงรายงานปริมาณขยะแยกตามประเภทขยะ
    activeUser: '/reports/active-user', // ดึงรายงานตามสัดส่วน User ที่ Active อยู่
    carbonReduction: '/reports/carbon-reduction', // ดึงรายงานปริมาณการลด Carbon footprint ของขยะรีไซเคิล
    carbonEmission: '/reports/carbon-emission', // ดึงรายงานปริมาณการปล่อย Carbon footprint ของขยะรีไซเคิลหลังนำไปกำจัด
    garbageCollectionLog: '/reports/garbage-collection-log', // ดึงรายงานการรวบรวมขยะ (ข้อมูลดิบ)
    order: '/reports/order', // ดึงรายงานการออเดอร์ของขยะ (ข้อมูลดิบ)
    redeamLog: '/reports/redeam-log', // ดึงรายงานการแลกของรางวัล
  },

  users: {
    get: '/users',
    create: '/users',
    getById: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    setRole: '/users/set-role',
    role: {
      switchCurrentRole: (id: string) => `/admin/user-roles/switch-role/current/${id}`,
      setCurrentRole: (id: string) => `/admin/user-roles/switch-role/${id}`,
      setRole: '/admin/user-roles',
      updateRole: (id: string) => `/admin/user-roles/${id}`,
      deleteRole: (id: string) => `/admin/user-roles/${id}`,
    },
  },

  userContact: {
    get: '/user-contacts',
    create: '/user-contacts',
    getById: (id: string) => `/user-contacts/${id}`,
    update: (id: string) => `/user-contacts/${id}`,
    delete: (id: string) => `/user-contacts/${id}`,
  },

  nodes: {
    get: '/admin/nodes',
    create: '/admin/nodes',
    getById: (id: string) => `/admin/nodes/${id}`,
    update: (id: string) => `/admin/nodes/${id}`,
    delete: (id: string) => `/admin/nodes/${id}`,
  },

  organize: {
    get: '/admin/organizes',
    create: '/admin/organizes',
    getById: (id: string) => `/admin/organizes/${id}`,
    update: (id: string) => `/admin/organizes/${id}`,
    delete: (id: string) => `/admin/organizes/${id}`,
  },

  article: {
    category: {
      get: '/admin/article-categories',
      create: '/admin/article-categories',
      getById: (id: string) => `/admin/article-categories/${id}`,
      update: (id: string) => `/admin/article-categories/${id}`,
      delete: (id: string) => `/admin/article-categories/${id}`,
    },
    get: '/admin/articles',
    create: '/admin/articles',
    getById: (id: string) => `/admin/articles/${id}`,
    update: (id: string) => `/admin/articles/${id}`,
    delete: (id: string) => `/admin/articles/${id}`,
  },

  token: {
    get: '/admin/tokens',
    create: '/admin/tokens',
    getById: (id: string) => `/admin/tokens/${id}`,
    update: (id: string) => `/admin/tokens/${id}`,
    delete: (id: string) => `/admin/tokens/${id}`,
  },

  order: {
    get: '/admin/orders',
    delete: (id: string) => `/admin/orders/${id}`,
  },

  quest: {
    get: '/admin/quests',
    create: '/admin/quests',
    getById: (id: string) => `/admin/quests/${id}`,
    update: (id: string) => `/admin/quests/${id}`,
    delete: (id: string) => `/admin/quests/${id}`,
  },

  questReward: {
    get: '/admin/quest-rewards',
    create: '/admin/quest-rewards',
    getById: (id: string) => `/admin/quest-rewards/${id}`,
    update: (id: string) => `/admin/quest-rewards/${id}`,
    delete: (id: string) => `/admin/quest-rewards/${id}`,
  },

  reward: {
    get: '/rewards',
    create: '/rewards',
    getById: (id: string) => `/rewards/${id}`,
    update: (id: string) => `/rewards/${id}`,
    delete: (id: string) => `/rewards/${id}`,
  },

  redeam: {
    get: '/redeam-logs',
    create: '/redeam-logs',
    getById: (id: string) => `/redeam-logs/${id}`,
    confirm: (id: string) => `/redeam-logs/confirm/${id}`,
    cancel: (id: string) => `/redeam-logs/cancel/${id}`,
  },

  garbage: {
    get: '/admin/garbages',
    create: '/admin/garbages',
    getById: (id: string) => `/admin/garbages/${id}`,
    update: (id: string) => `/admin/garbages/${id}`,
    delete: (id: string) => `/admin/garbages/${id}`,
    type: {
      get: '/admin/garbage-types',
      create: '/admin/garbage-types',
      getById: (id: string) => `/admin/garbage-types/${id}`,
      update: (id: string) => `/admin/garbage-types/${id}`,
      delete: (id: string) => `/admin/garbage-types/${id}`,
    },
  },

  garbageCollection: {
    log: {
      get: '/admin/garbage-collection-logs',
      create: '/admin/garbage-collection-logs',
      getById: (id: string) => `/admin/garbage-collection-logs/${id}`,
      update: (id: string) => `/admin/garbage-collection-logs/${id}`,
      delete: (id: string) => `/admin/garbage-collection-logs/${id}`,
    },
    manageBy: {
      get: '/admin/garbage-collection-manages-by',
      create: '/admin/garbage-collection-manages-by',
      getById: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
      update: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
      delete: (id: string) => `/admin/garbage-collection-manages-by/${id}`,
    },
    manageType: {
      get: '/admin/garbage-collection-manage-types',
      create: '/admin/garbage-collection-manage-types',
      getById: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
      update: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
      delete: (id: string) => `/admin/garbage-collection-manage-types/${id}`,
    },
    location: {
      get: '/admin/locations',
      create: '/admin/locations',
      getById: (id: string) => `/admin/locations/${id}`,
      update: (id: string) => `/admin/locations/${id}`,
      delete: (id: string) => `/admin/locations/${id}`,
    },
    locationType: {
      get: '/admin/location-types',
      create: '/admin/location-types',
      getById: (id: string) => `/admin/location-types/${id}`,
      update: (id: string) => `/admin/location-types/${id}`,
      delete: (id: string) => `/admin/location-types/${id}`,
    },
  },

  theme: {
    get: '/themes',
    create: '/themes',
    getById: (id: string) => `/themes/${id}`,
    update: (id: string) => `/themes/${id}`,
    delete: (id: string) => `/themes/${id}`,
  },

  banner: {
    get: '/banners',
    create: '/banners',
    getById: (id: string) => `/banners/${id}`,
    update: (id: string) => `/banners/${id}`,
    delete: (id: string) => `/banners/${id}`,
  },

  invitationCode: {
    get: '/invitation-codes',
    create: '/invitation-codes',
    update: (id: string) => `/invitation-codes/${id}`,
    logs: {
      get: '/invitation-code-logs/',
    },
  },
};
